/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { HoverEffectWrapper } from '@coursera-web/cds-labs';

import Imgix from 'js/components/Imgix';

import { Link, Typography2, VisuallyHidden, breakpoints, typography2 as typography } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import annularSectorImage from 'images/front-page/AnnularSector.svg';
import linesImage from 'images/front-page/Lines.png';

type MerchandisingBucketProps = {
  title?: string;
  linkTo?: string;
  index?: number;
  image?: string;
  dataTestId?: string;
};

const styles = {
  container: css`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    border: none;
    border-radius: calc(var(--cds-border-radius-200) + var(--cds-border-radius-25));
    background: linear-gradient(278deg, var(--cds-color-purple-25) 17.86%, var(--cds-color-blue-25) 75.22%);

    ${breakpoints.down('xs')} {
      border-radius: calc(var(--cds-border-radius-200) - var(--cds-border-radius-50));
    }
  `,
  link: css`
    display: flex;
    min-height: 110px;
    flex-direction: row;
    align-items: center;
    padding: var(--cds-spacing-200);
    text-decoration: none;
    background-image: url(${linesImage}), url(${annularSectorImage});
    background-repeat: no-repeat, no-repeat;
    background-position: center right 40px, center right 20px;
    background-size: 95px auto, auto 93px;

    ${breakpoints.down('xs')} {
      min-height: 87px;
    }

    ${breakpoints.down('xs')} {
      background-position: center right 20px, center right;
      background-size: 47px auto, auto 93px;
    }
  `,
  title: css`
    flex-grow: 1;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    ${typography.subtitleLarge};
    ${breakpoints.down('xs')} {
      ${typography.subtitleSmall};
    }
  `,
  image: css`
    width: 50px;
    margin-right: var(--cds-border-radius-300);

    ${breakpoints.down('xs')} {
      width: 32px;
      margin-right: 0;
    }
  `,
};

const MerchandisingBucket = ({ title, linkTo, image, index, dataTestId }: MerchandisingBucketProps) => {
  const track = useTracker();
  const visibilityTracker: React.MutableRefObject<HTMLAnchorElement | null> = useVisibilityTracker(
    'view_bucket_card',
    {
      bucketCardIndex: index ?? 0,
      bucketCardName: title,
      bucketCardLink: linkTo,
    },
    [],
    { fullyVisible: true }
  );

  const handleClick = () => {
    track('click_button', {
      button: {
        name: 'lohp_bucket',
        linkType: 'internal',
        linkName: 'lohp_bucket_link',
        linkURL: linkTo,
      },
    });
  };

  if (!title) return null;

  return (
    <HoverEffectWrapper css={styles.container} data-testid={dataTestId}>
      <Link css={styles.link} href={linkTo} ref={visibilityTracker} onClick={handleClick}>
        {/* For screen readers, the Typography2 element announces the heading level,
        which adds redundant and confusing information for users, so the visually hidden
        element is announced instead. */}
        <VisuallyHidden>{title}</VisuallyHidden>
        <Typography2 component="h2" css={styles.title} aria-hidden="true">
          {title}
        </Typography2>
        {image && <Imgix src={image} alt="" css={styles.image} maxWidth={50} aria-hidden="true" />}
      </Link>
    </HoverEffectWrapper>
  );
};

export default MerchandisingBucket;
